// Root application component. Defines routing, auth context, role-based route guards, and onboarding flow.
import { useState, useRef, createContext, useContext, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { clearAuthState, validateSession } from '@/services/apiClient';
import {StudentDashboard} from '@/components/StudentsView/StudentDashboard';
import TeacherDashboard from "@/components/TeacherView/TeacherDashboard";
import AdminDashboard from '@/components/AdminSystem/AdminDashboard';
// import { LandingPage } from '@/components/LandingPage';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AuthCallback } from '@/components/AuthCallback';
import { OnboardingFlow } from '@/components/Onboarding/OnboardingFlow';
import { useFirstTimeUser } from '@/hooks/useFirstTimeUser';
import SchoolAdminDashboard from './components/SchoolAdmin/SchoolAdminDashboard';
import { InviteAccept } from '@/components/InviteAccept';
import { UnifiedLogin } from '@/components/Login';
import { ResetPassword } from '@/components/ResetPassword';
import { ErrorBoundary } from '@/components/ErrorBoundary';


type UserRole = 'student' | 'teacher' | 'admin';

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  schoolId?: string;
}

interface AppContextType {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  logout: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

// Function to determine user role based on email domain
const getUserRoleFromEmail = (email: string, selectedRole?: string): UserRole => {
  if (selectedRole) {
    if (selectedRole === 'admin') return 'admin';
    if (selectedRole === 'teacher') return 'teacher';
    if (selectedRole === 'student') return 'student';
  }
  
  // Fallback to email-based detection if no role was selected
  if (email.includes('school-admin') || email.includes('principal')) {
    return 'admin';
  } else if (email.includes('teacher') || email.includes('faculty') || email.includes('staff')) {
    return 'teacher';
  } else {
    return 'student';
  }
};

// Protected Route component
function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: UserRole[] }) {
  const { user } = useApp();
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen min-w-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!currentUser || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

// C3/H11: Server-side session validation hook — runs once per route guard mount.
// On 401, clears auth state and redirects to login to prevent localStorage role tampering.
function useServerSessionCheck(expectedRole?: string | string[]) {
  const navigate = useNavigate();
  const checked = useRef(false);
  useEffect(() => {
    if (checked.current) return;
    checked.current = true;
    validateSession().then(serverRole => {
      if (serverRole === null) {
        clearAuthState();
        navigate('/login', { replace: true });
      } else if (expectedRole) {
        const allowed = Array.isArray(expectedRole) ? expectedRole : [expectedRole];
        if (!allowed.includes(serverRole)) {
          clearAuthState();
          navigate('/login', { replace: true });
        }
      }
    });
  }, []);
}

// System Admin Route component (uses localStorage auth + server validation)
function SystemAdminRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  useServerSessionCheck('super_admin');

  const authToken = sessionStorage.getItem('authToken');
  const userRole = localStorage.getItem('userRole');
  const user = localStorage.getItem('user');

  if (!authToken || userRole !== 'super_admin' || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// School Admin Route component (uses localStorage auth + server validation)
function SchoolAdminRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  useServerSessionCheck('admin');

  const authToken = sessionStorage.getItem('authToken');
  const userRole = localStorage.getItem('userRole');
  const user = localStorage.getItem('user');

  if (!authToken || userRole !== 'admin' || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// User Route component (students/teachers, uses localStorage auth + server validation)
function UserRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles: ('student' | 'teacher')[] }) {
  const location = useLocation();
  useServerSessionCheck(allowedRoles);

  const authToken = sessionStorage.getItem('authToken');
  const userRole = localStorage.getItem('userRole');
  const user = localStorage.getItem('user');

  if (!authToken || !userRole || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!allowedRoles.includes(userRole as 'student' | 'teacher')) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Onboarding Route Component
function OnboardingRoute() {
  const { user } = useApp();
  const { markOnboardingComplete } = useFirstTimeUser();
  const navigate = useNavigate();

  const handleOnboardingComplete = async (data: { notificationsGranted: boolean; permissions: Record<string, boolean> }) => {
    await markOnboardingComplete(data);
    // Redirect to dashboard after onboarding is complete
    navigate('/dashboard', { replace: true });
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <OnboardingFlow
      userRole={user.role}
      onComplete={handleOnboardingComplete}
    />
  );
}

// Dashboard component that routes to correct dashboard based on role
function Dashboard() {
  const { user } = useApp();
  const { currentUser, loading } = useAuth();
  const { isFirstTime, isLoading: onboardingLoading, markOnboardingComplete } = useFirstTimeUser();
  
  if (loading || onboardingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
          <p className="mt-2 text-sm text-gray-500">
            {loading ? 'Authenticating...' : 'Checking onboarding status...'}
          </p>
        </div>
      </div>
    );
  }
  
  if (!currentUser || !user) {
    return <Navigate to="/login" replace />;
  }

  // Show onboarding flow for first-time users
  if (isFirstTime) {
    return (
      <OnboardingFlow
        userRole={user.role}
        onComplete={async (data) => {
          await markOnboardingComplete(data);
          // The component will re-render and show the dashboard after state update
        }}
      />
    );
  }
  
  switch (user.role) {
    case 'student':
      return <StudentDashboard />;
    case 'teacher':
      return <TeacherDashboard />;
    case 'admin':
      return <SchoolAdminDashboard />;

  }
}

// Synchronously read user from sessionStorage/localStorage so it's available on first render.
// This prevents a flash where child components call useApp() and get user === null.
function getInitialUser(): User | null {
  try {
    const authToken = sessionStorage.getItem('authToken');
    const userRole = localStorage.getItem('userRole');
    const userStr = localStorage.getItem('user');
    if (!authToken || !userRole || !userStr) return null;

    const userData = JSON.parse(userStr);
    const effectiveRole = (userData.role || userRole) as UserRole;

    const validRoles: string[] = ['student', 'teacher', 'admin', 'super_admin'];
    if (!validRoles.includes(effectiveRole)) return null;

    // Self-heal the standalone key if mismatched
    if (userData.role && userData.role !== userRole) {
      localStorage.setItem('userRole', userData.role);
    }

    return {
      id: userData.id || 'local-user',
      name: userData.name || userData.email?.split('@')[0] || 'User',
      email: userData.email || '',
      role: effectiveRole,
      schoolId: userData.schoolId || undefined,
    };
  } catch {
    return null;
  }
}

function AppContent() {
  const [user, setUser] = useState<User | null>(getInitialUser);
  const { currentUser, logout: authLogout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Sync user from sessionStorage/localStorage or Supabase whenever the
  // route changes (covers post-login navigate) or Supabase auth changes.
  useEffect(() => {
    // Re-read storage on every route change — catches post-login navigates
    const freshUser = getInitialUser();
    if (freshUser) {
      setUser(freshUser);
      return;
    }
    
    // Fall back to Supabase auth
    if (currentUser) {
      // For Supabase-only sessions, email-based role detection is the safe
      // fallback.  Only use localStorage.userRole if it is a valid student/teacher
      // role – never promote a Supabase user to super_admin via a stale key.
      const storedRole  = localStorage.getItem('userRole');
      const safeRoles   = ['student', 'teacher', 'admin'];
      const resolvedRole = (storedRole && safeRoles.includes(storedRole))
        ? storedRole as UserRole
        : getUserRoleFromEmail(currentUser.email || '');

      const appUser: User = {
        id: currentUser.id,
        name: currentUser.user_metadata?.full_name || currentUser.email?.split('@')[0] || 'User',
        email: currentUser.email || '',
        role: resolvedRole,
      };
      setUser(appUser);
    } else {
      setUser(null);
    }
  }, [currentUser, location.pathname]);

  const logout = async () => {
    try {
      await authLogout();
      setUser(null);
      // Clear localStorage auth data
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('refreshToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('user');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const contextValue: AppContextType = {
    user,
    setUser,
    logout
  };

  return (
    <AppContext.Provider value={contextValue}>
      <div className="min-h-screen bg-background">
        <Routes>
          <Route path="/" element={<UnifiedLogin />} />
          <Route path="/login" element={<UnifiedLogin />} />
          {/* Legacy routes redirect to unified login */}
          <Route path="/login/select" element={<Navigate to="/login" replace />} />
          <Route path="/login/system-admin" element={<Navigate to="/login" replace />} />
          <Route path="/login/school-admin" element={<Navigate to="/login" replace />} />
          <Route path="/login/teacher" element={<Navigate to="/login" replace />} />
          <Route path="/login/student" element={<Navigate to="/login" replace />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/invite" element={<InviteAccept />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/student/*" 
            element={
              <UserRoute allowedRoles={['student']}>
                <StudentDashboard />
              </UserRoute>
            } 
          />
          <Route 
            path="/teacher/*" 
            element={
              <UserRoute allowedRoles={['teacher']}>
                <TeacherDashboard />
              </UserRoute>
            } 
          />
          <Route 
            path="/admin/*" 
            element={
              <SchoolAdminRoute>
                <SchoolAdminDashboard />
              </SchoolAdminRoute>
            } 
          />
          {/* <Route path="/demo/onboarding" element={<OnboardingDemo />} /> */}
          <Route 
            path="/onboarding" 
            element={
              <ProtectedRoute>
                <OnboardingRoute />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/system-admin/*" 
            element={
              <SystemAdminRoute>
                <AdminDashboard />
              </SystemAdminRoute>
            } 
          />
          {/* Backward compatibility: redirect '/admin' to '/system-admin' */}
          {/* <Route path="/admin/*" element={<Navigate to="/system-admin" replace />} /> */}
          {/* Redirect any unmatched routes to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>


    </AppContext.Provider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}